import { CheckBadgeIcon, ChevronLeftIcon, ChevronRightIcon, ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import clsx from 'clsx'
import { useParams } from 'react-router-dom'
import { Badge } from '../catalyst/badge'
import { Heading, Subheading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { isBlueberry, usePesticideDetail } from '../lib/pesticides'
import type { PesticideDetail, PesticideLabel, PesticideLabelStage, PesticideUsePattern } from '../lib/types'
import { Shell } from './shell'

const SIGNAL_WORD_COLOR = {
  CAUTION: 'yellow',
  WARNING: 'orange',
  DANGER: 'red',
  POISON: 'rose',
} as const

const STAGES: { stage: PesticideLabelStage; title: string; detail: string }[] = [
  { stage: 'fetch_label', title: 'Finding the label', detail: "Looking up this product's label with Health Canada." },
  { stage: 'extract_label', title: 'Reading the label', detail: 'Pulling the text out of the label document.' },
  { stage: 'structure_label', title: 'Extracting application details', detail: 'Finding rates, timing, and pre-harvest and re-entry intervals for each crop.' },
]

function Spinner() {
  return (
    <svg className="size-5 shrink-0 animate-spin text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z" />
    </svg>
  )
}

/**
 * Shown while the label is being fetched and read. The first visit to a product does this work, so
 * it can take up to a minute; a spinner, the current step, and placeholder rows make it clear that
 * something is happening and where.
 */
function LabelProgressPanel({ stage, timedOut }: { stage: PesticideLabelStage; timedOut: boolean }) {
  const current = STAGES.findIndex((s) => s.stage === stage)

  if (timedOut) {
    return (
      <div className="mt-8 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/40">
        <Text className="text-amber-800 dark:text-amber-300">
          This is taking longer than expected. Reload the page in a minute to check again. Always check the physical product label before use.
        </Text>
      </div>
    )
  }

  return (
    <div className="mt-8" role="status" aria-live="polite">
      <div className="flex items-center gap-3">
        <Subheading>Application details</Subheading>
        <Badge color="amber">Preparing</Badge>
      </div>
      <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/40">
        <div className="flex items-center gap-3">
          <Spinner />
          <Text className="font-medium text-amber-900 dark:text-amber-200">Getting this product's application details…</Text>
        </div>
        <Text className="mt-1 text-amber-800 dark:text-amber-300">
          This is the first time this product has been opened, so we're reading its Health Canada label now. It usually takes under a minute and appears here
          automatically. Always check the physical product label before use.
        </Text>
        <ol className="mt-4 space-y-2">
          {STAGES.map((s, i) => (
            <li key={s.stage} className="flex items-start gap-3">
              <span
                className={
                  'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-medium ' +
                  (i < current
                    ? 'bg-green-600 text-white'
                    : i === current
                      ? 'bg-amber-500 text-white'
                      : 'bg-zinc-950/10 text-zinc-500 dark:bg-white/10 dark:text-zinc-400')
                }
              >
                {i < current ? '✓' : i + 1}
              </span>
              <div>
                <Text className={i === current ? 'font-medium text-zinc-950 dark:text-white' : ''}>{s.title}</Text>
                {i === current && <Text className="text-sm">{s.detail}</Text>}
              </div>
            </li>
          ))}
        </ol>
      </div>
      <div className="mt-4 space-y-4" aria-hidden="true">
        {[0, 1].map((i) => (
          <div key={i} className="animate-pulse rounded-lg border border-zinc-950/10 p-4 dark:border-white/10">
            <div className="h-4 w-1/3 rounded bg-zinc-950/10 dark:bg-white/10" />
            <div className="mt-4 h-3 w-full rounded bg-zinc-950/10 dark:bg-white/10" />
            <div className="mt-2 h-3 w-5/6 rounded bg-zinc-950/10 dark:bg-white/10" />
            <div className="mt-2 h-3 w-2/3 rounded bg-zinc-950/10 dark:bg-white/10" />
          </div>
        ))}
      </div>
    </div>
  )
}

/** The caution shown when there is no application data and none is coming: always check the physical label. */
function LabelPendingNotice({ status }: { status: 'failed' | 'not_found' | 'not_fetched' | 'unavailable' }) {
  const message =
    status === 'failed'
      ? "We couldn't read this product's label just now. Try again later, and always check the physical product label before use."
      : status === 'not_found'
        ? "Health Canada's label database has no label on file for this registration. That does not mean there are no restrictions — always check the physical product label before use."
        : status === 'unavailable'
          ? 'Application details for this product were withheld after review. Always check the physical product label before use.'
          : 'This is registry information only. Application rate, timing, pre-harvest and re-entry intervals, and mixing and safety notes are not available — always check the product label before use.'

  return (
    <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/40">
      <Text className="text-amber-800 dark:text-amber-300">{message}</Text>
    </div>
  )
}

type ReadyLabel = Extract<PesticideLabel, { status: 'ready' }>

/** Raw registry fields are semicolon-joined lists; split them so they can be scanned as chips. */
function splitList(raw: string | null): string[] {
  return (raw ?? '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
}

function pluralDays(n: number) {
  return `${n} day${n === 1 ? '' : 's'}`
}

/** "3 days", or "3–7 days" when values differ across rows. `fmt` renders the number-plus-suffix for one value. */
function formatRange(values: number[], fmt: (n: number, withUnit: boolean) => string) {
  if (values.length === 0) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  return min === max ? fmt(min, true) : `${fmt(min, false)}–${fmt(max, true)}`
}

/** A small label + value cell; `muted` marks a value the label doesn't state. */
function Stat({ label, children, muted, className }: { label: string; children: React.ReactNode; muted?: boolean; className?: string }) {
  return (
    <div className={clsx('min-w-0', className)}>
      <div className="text-xs/5 font-medium tracking-wide text-zinc-500 uppercase dark:text-zinc-400">{label}</div>
      <div className={clsx('text-base/6 font-semibold', muted ? 'font-normal text-zinc-400 dark:text-zinc-500' : 'text-zinc-950 dark:text-white')}>
        {children}
      </div>
    </div>
  )
}

function Section({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 border-b border-zinc-950/10 pb-2 dark:border-white/10">
        <Subheading level={2}>{title}</Subheading>
        {aside && <div className="text-sm/6 text-zinc-500 dark:text-zinc-400">{aside}</div>}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  )
}

/** A short list of chips; long lists show the first few and fold the rest into a disclosure (nothing is dropped). */
function ChipList({ items, limit = 10, color = 'zinc' }: { items: string[]; limit?: number; color?: 'zinc' | 'blue' | 'green' }) {
  if (items.length === 0) return <span className="text-zinc-400 dark:text-zinc-500">—</span>
  const head = items.slice(0, limit)
  const tail = items.slice(limit)
  return (
    <div className="flex flex-wrap gap-1.5">
      {head.map((item) => (
        <Badge key={item} color={color}>
          {item}
        </Badge>
      ))}
      {tail.length > 0 && (
        <details className="contents [&[open]>summary]:hidden">
          <summary className="cursor-pointer list-none rounded-md px-1.5 py-0.5 text-sm/5 font-medium text-blue-600 select-none hover:underline dark:text-blue-400">
            +{tail.length} more
          </summary>
          {tail.map((item) => (
            <Badge key={item} color={color}>
              {item}
            </Badge>
          ))}
        </details>
      )}
    </div>
  )
}

/**
 * The numbers a grower needs before anything else, pulled from the label: how toxic it is, how long
 * to stay out, how long before harvest, and which resistance group to rotate. Blueberry rows drive
 * the pre-harvest figure when the label has any; otherwise it spans every crop on the label.
 */
function GlanceStrip({ label }: { label: ReadyLabel }) {
  const berry = label.usePatterns.filter(isBlueberry)
  const scope = berry.length > 0 ? berry : label.usePatterns
  const phi = formatRange(
    scope.flatMap((p) => (p.phiDays === null ? [] : [p.phiDays])),
    (n, withUnit) => (withUnit ? pluralDays(n) : String(n)),
  )
  const maxApps = formatRange(
    scope.flatMap((p) => (p.maxApplicationsPerSeason === null ? [] : [p.maxApplicationsPerSeason])),
    (n, withUnit) => (withUnit ? `${n} per season` : String(n)),
  )

  return (
    <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 rounded-xl bg-zinc-950/[0.03] p-4 ring-1 ring-zinc-950/5 sm:grid-cols-4 dark:bg-white/5 dark:ring-white/10">
      <Stat label="Signal word" muted={!label.signalWord}>
        {label.signalWord ? <Badge color={SIGNAL_WORD_COLOR[label.signalWord]}>{label.signalWord}</Badge> : 'Not stated'}
      </Stat>
      <Stat label="Re-entry (REI)" muted={label.reiHours === null && !label.reiText}>
        {label.reiHours !== null ? `${label.reiHours} hours` : (label.reiText ?? 'Not stated')}
      </Stat>
      <Stat label={berry.length > 0 ? 'Pre-harvest · blueberry' : 'Pre-harvest (PHI)'} muted={!phi}>
        {phi ?? 'Not stated'}
      </Stat>
      {maxApps ? (
        <Stat label="Max applications">{maxApps}</Stat>
      ) : label.resistanceManagementGroup ? (
        <Stat label="Resistance group">{label.resistanceManagementGroup}</Stat>
      ) : (
        <Stat label="Max applications" muted>
          Not stated
        </Stat>
      )}
    </div>
  )
}

/** One crop's application details: numbers first, then the prose, then the citation. */
function UsePatternCard({ pattern, highlight }: { pattern: PesticideUsePattern; highlight?: boolean }) {
  const pests = splitList(pattern.pestRaw)
  const hasNumbers = pattern.phiDays !== null || pattern.minRetreatmentIntervalDays !== null || pattern.maxApplicationsPerSeason !== null

  return (
    <div
      className={clsx(
        'rounded-xl border p-4',
        highlight ? 'border-blue-600/30 bg-blue-50/40 dark:border-blue-400/30 dark:bg-blue-500/5' : 'border-zinc-950/10 dark:border-white/10',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="text-base/6 font-semibold text-zinc-950 dark:text-white">{pattern.cropRaw}</h3>
        {pattern.cropGroup && pattern.cropGroup !== pattern.cropRaw && <Badge color="zinc">{pattern.cropGroup}</Badge>}
      </div>
      {pests.length > 0 && (
        <div className="mt-2 flex flex-wrap items-start gap-2">
          <span className="pt-0.5 text-xs/5 font-medium tracking-wide text-zinc-500 uppercase dark:text-zinc-400">For</span>
          <ChipList items={pests} limit={8} />
        </div>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-[1.6fr_1fr]">
        <Stat label="Rate">
          <span className="font-semibold">{pattern.applicationRateText}</span>
        </Stat>
        {hasNumbers && (
          <div className="grid grid-cols-3 gap-3">
            <Stat label="PHI" muted={pattern.phiDays === null}>
              {pattern.phiDays !== null ? pluralDays(pattern.phiDays) : '—'}
            </Stat>
            <Stat label="Re-treat" muted={pattern.minRetreatmentIntervalDays === null}>
              {pattern.minRetreatmentIntervalDays !== null ? `${pattern.minRetreatmentIntervalDays} d` : '—'}
            </Stat>
            <Stat label="Max apps" muted={pattern.maxApplicationsPerSeason === null}>
              {pattern.maxApplicationsPerSeason ?? '—'}
            </Stat>
          </div>
        )}
      </div>

      {(pattern.applicationMethod || pattern.timingText) && (
        <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm/6 sm:grid-cols-2">
          {pattern.applicationMethod && (
            <div>
              <dt className="text-xs/5 font-medium tracking-wide text-zinc-500 uppercase dark:text-zinc-400">Method</dt>
              <dd className="text-zinc-950 dark:text-white">{pattern.applicationMethod}</dd>
            </div>
          )}
          {pattern.timingText && (
            <div>
              <dt className="text-xs/5 font-medium tracking-wide text-zinc-500 uppercase dark:text-zinc-400">Timing</dt>
              <dd className="text-zinc-950 dark:text-white">{pattern.timingText}</dd>
            </div>
          )}
        </dl>
      )}

      {pattern.restrictionsText && (
        <div className="mt-4 flex gap-2 rounded-lg bg-amber-50 p-3 text-sm/6 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
          <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" />
          <div>
            <span className="font-medium">Restrictions. </span>
            {pattern.restrictionsText}
          </div>
        </div>
      )}

      <details className="group mt-3 text-sm/6 text-zinc-500 dark:text-zinc-400">
        <summary className="inline-flex cursor-pointer list-none items-center gap-1 select-none hover:text-zinc-700 dark:hover:text-zinc-200">
          <ChevronRightIcon className="size-4 transition-transform group-open:rotate-90" />
          Label citation
        </summary>
        <blockquote className="mt-2 border-l-2 border-zinc-950/10 pl-3 italic dark:border-white/10">"{pattern.sourceExcerpt}"</blockquote>
      </details>
    </div>
  )
}

function ReviewNote({ label }: { label: ReadyLabel }) {
  const unreviewed = label.reviewStatus === 'unreviewed'
  return (
    <div
      className={clsx(
        'mt-4 flex gap-2 rounded-lg px-3 py-2 text-sm/6',
        unreviewed ? 'bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200' : 'bg-zinc-950/[0.03] text-zinc-600 dark:bg-white/5 dark:text-zinc-400',
      )}
    >
      {unreviewed ? <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" /> : <CheckBadgeIcon className="mt-0.5 size-4 shrink-0" />}
      <p>
        {unreviewed ? (
          <>
            <span className="font-medium">Unreviewed. </span>
            Extracted automatically from the Health Canada label; a person hasn't checked it yet, so it may contain mistakes.
          </>
        ) : (
          <>
            <span className="font-medium">Reviewed. </span>
            Extracted from the Health Canada label and checked before publishing.
          </>
        )}{' '}
        Always confirm against the current physical label before applying.
      </p>
    </div>
  )
}

/** PPE, environmental hazards and resistance group: the "before you mix" facts, side by side. */
function SafetySection({ label }: { label: ReadyLabel }) {
  const items: { term: string; value: string }[] = []
  if (label.ppeMixingLoading) items.push({ term: 'PPE · mixing & loading', value: label.ppeMixingLoading })
  if (label.ppeApplication) items.push({ term: 'PPE · application', value: label.ppeApplication })
  if (label.reiText && label.reiHours !== null) items.push({ term: 'Re-entry detail', value: label.reiText })
  if (label.environmentalHazardsText) items.push({ term: 'Environmental hazards', value: label.environmentalHazardsText })
  if (items.length === 0 && !label.resistanceManagementGroup) return null

  return (
    <Section title="Safety & handling" aside={label.resistanceManagementGroup ? <>Resistance group <Badge color="blue">{label.resistanceManagementGroup}</Badge></> : undefined}>
      <dl className="grid gap-x-8 gap-y-4 text-sm/6 md:grid-cols-2">
        {items.map((i) => (
          <div key={i.term} className={i.term === 'Environmental hazards' ? 'md:col-span-2' : undefined}>
            <dt className="text-xs/5 font-medium tracking-wide text-zinc-500 uppercase dark:text-zinc-400">{i.term}</dt>
            <dd className="text-zinc-950 dark:text-white">{i.value}</dd>
          </div>
        ))}
      </dl>
    </Section>
  )
}

/** The structured application data, marked as reviewed or not — never the raw label text. */
function LabelApplicationDetails({ label }: { label: ReadyLabel }) {
  const berry = label.usePatterns.filter(isBlueberry)
  const other = label.usePatterns.filter((p) => !isBlueberry(p))

  return (
    <>
      <ReviewNote label={label} />
      <GlanceStrip label={label} />

      <Section
        title="How to apply"
        aside={`${label.usePatterns.length} crop use${label.usePatterns.length === 1 ? '' : 's'} on this label`}
      >
        {label.usePatterns.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-950/15 p-6 text-center dark:border-white/15">
            <Text>No crop-specific application details were found on this label. Always check the physical label before use.</Text>
          </div>
        ) : (
          <div className="space-y-4">
            {berry.map((p) => (
              <UsePatternCard key={p.id} pattern={p} highlight />
            ))}
            {other.length > 0 && berry.length > 0 ? (
              <details className="group rounded-xl border border-zinc-950/10 p-4 dark:border-white/10">
                <summary className="flex cursor-pointer list-none items-center gap-1 text-sm/6 font-medium text-zinc-700 select-none dark:text-zinc-200">
                  <ChevronRightIcon className="size-4 transition-transform group-open:rotate-90" />
                  Other crops on this label ({other.length})
                </summary>
                <div className="mt-4 space-y-4">
                  {other.map((p) => (
                    <UsePatternCard key={p.id} pattern={p} />
                  ))}
                </div>
              </details>
            ) : (
              other.map((p) => <UsePatternCard key={p.id} pattern={p} />)
            )}
          </div>
        )}
      </Section>

      <SafetySection label={label} />

      <Text className="mt-6 text-xs">
        Source: Health Canada label document, fetched {new Date(label.fetchedAt).toLocaleDateString()}.
      </Text>
    </>
  )
}

function RegistryRow({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 py-2.5 sm:grid-cols-[10rem_1fr] sm:gap-4">
      <dt className="text-sm/6 text-zinc-500 dark:text-zinc-400">{term}</dt>
      <dd className="min-w-0 text-sm/6 text-zinc-950 dark:text-white">{children || <span className="text-zinc-400 dark:text-zinc-500">—</span>}</dd>
    </div>
  )
}

/** Everything the registry knows, kept in full but compact: identity facts as a tight list, long lists as chips. */
function RegistrySection({ pesticide }: { pesticide: PesticideDetail }) {
  return (
    <Section title="Registry record" aside="Health Canada PPID">
      <dl className="divide-y divide-zinc-950/5 dark:divide-white/5">
        <RegistryRow term="Registrant">{pesticide.registrantName}</RegistryRow>
        <RegistryRow term="Type">{pesticide.productType}</RegistryRow>
        <RegistryRow term="Marketing type">{pesticide.marketingType}</RegistryRow>
        <RegistryRow term="First registered">{pesticide.dateFirstRegistered}</RegistryRow>
        <RegistryRow term="Expires">{pesticide.expiryDate}</RegistryRow>
        <RegistryRow term="Use site category">
          <ChipList items={splitList(pesticide.useSiteCategory)} />
        </RegistryRow>
        <RegistryRow term="Sites of use">
          <ChipList items={splitList(pesticide.sitesOfUse)} limit={12} />
        </RegistryRow>
        <RegistryRow term="Pests">
          <ChipList items={splitList(pesticide.pests)} limit={12} />
        </RegistryRow>
      </dl>
    </Section>
  )
}

export function PesticidePage() {
  const { pesticideId } = useParams<{ pesticideId: string }>()
  const { pesticide, loading, error, pollTimedOut } = usePesticideDetail(pesticideId)

  return (
    <Shell>
      <Link href="/pesticides" className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        Pesticides
      </Link>

      {error ? (
        <div className="mt-8 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading || !pesticide ? (
        <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading…</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Heading>{pesticide.productNameEn}</Heading>
              <p className="mt-1 text-sm/6 text-zinc-500 dark:text-zinc-400">
                {[pesticide.registrantName, `Reg. no. ${pesticide.registrationNumber}`, pesticide.productType].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
              <Badge color={pesticide.currentOrHistorical === 'Current' ? 'green' : 'zinc'}>{pesticide.currentOrHistorical}</Badge>
              {pesticide.registrationStatus && pesticide.registrationStatus !== pesticide.currentOrHistorical && (
                <Badge color="zinc">{pesticide.registrationStatus}</Badge>
              )}
            </div>
          </div>
          {pesticide.activeIngredientsRawEn && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs/5 font-medium tracking-wide text-zinc-500 uppercase dark:text-zinc-400">Active</span>
              <ChipList items={splitList(pesticide.activeIngredientsRawEn)} color="blue" limit={6} />
            </div>
          )}

          {pesticide.label.status === 'ready' ? (
            <LabelApplicationDetails label={pesticide.label} />
          ) : pesticide.label.status === 'processing' ? (
            <LabelProgressPanel stage={pesticide.label.stage} timedOut={pollTimedOut} />
          ) : (
            <LabelPendingNotice status={pesticide.label.status} />
          )}

          <RegistrySection pesticide={pesticide} />
        </>
      )}
    </Shell>
  )
}
